import { auth } from "../firebase/client";
import { uploadPlanFile, deletePlanFile, cancelPlanUpload } from "../billing/planService";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Preferences } from "@capacitor/preferences";

export const MAX_UPLOAD_BYTES = 1_000_000;

const STATE_KEY = "tripai.uploadQueue.state.v4";
const QUEUE_KEY = "tripai.uploadQueue.v3";
const LEGACY_QUEUE_KEYS = ["tripai.uploadQueue.v2"];
const QUEUE_DIR = "tripai-upload-queue";
const COMPLETED_KEY = "tripai.uploadQueue.completed.v1";
let activeFlush: Promise<Array<{ id: string; url: string }>> | null = null;

export interface QueuedUpload {
  id: string;
  storagePath: string;
  localPath: string;
  name: string;
  type: string;
  size: number;
  createdAt: string;
  restoreEpoch?: string;
  attempts: number;
  lastAttemptAt?: string;
  lastErrorCode?: string;
  lastErrorMessage?: string;
}

export interface UploadQueueStatus {
  state: "pending" | "failed" | "completed";
  attempts: number;
  url?: string;
  lastAttemptAt?: string;
  lastErrorCode?: string;
  lastErrorMessage?: string;
}

function bytesToBase64(bytes: Uint8Array): string {
  let value = "";
  const chunk = 0x8000;
  for (let index = 0; index < bytes.length; index += chunk) {
    value += String.fromCharCode(...bytes.subarray(index, index + chunk));
  }
  return btoa(value);
}

function base64ToBlob(value: string, type: string): Blob {
  const raw = atob(value);
  const bytes = new Uint8Array(raw.length);
  for (let index = 0; index < raw.length; index += 1) bytes[index] = raw.charCodeAt(index);
  return new Blob([bytes], { type });
}

async function dataToBlob(data: string | Blob, type: string): Promise<Blob> {
  return data instanceof Blob ? data : base64ToBlob(data, type);
}

function normalizeQueueItem(value: unknown): QueuedUpload | null {
  if (!value || typeof value !== "object") return null;
  const item = value as Partial<QueuedUpload>;
  if (!item.id || !item.storagePath || !item.localPath) return null;
  return {
    id: String(item.id),
    storagePath: String(item.storagePath),
    localPath: String(item.localPath),
    name: String(item.name || "file"),
    type: String(item.type || "application/octet-stream"),
    size: Number(item.size || 0),
    createdAt: String(item.createdAt || new Date().toISOString()),
    attempts: Number(item.attempts || 0),
    lastAttemptAt: item.lastAttemptAt ? String(item.lastAttemptAt) : undefined,
    lastErrorCode: item.lastErrorCode ? String(item.lastErrorCode) : undefined,
    lastErrorMessage: item.lastErrorMessage ? String(item.lastErrorMessage) : undefined
  };
}

const restoreContexts = new Map<string,string>();
export function setBackupRestoreContext(uid:string,operationId:unknown):void {
  restoreContexts.set(uid,typeof operationId==="string"&&/^[a-f0-9-]{36}$/.test(operationId)?operationId:"");
}
interface CloudCleanup { path: string; token: string; restoreEpoch?:string }
interface QueueState { queue: QueuedUpload[]; completed: Record<string, string>; cleanup?: CloudCleanup[]; cancellations?: Array<{ path: string; operationId: string }> }
let mutationTail: Promise<unknown> = Promise.resolve();
function serialized<T>(task: () => Promise<T>): Promise<T> {
  const result = mutationTail.then(async (): Promise<T> => {
    // Coordinate persistence across Web tabs where the browser provides Web Locks.
    if (typeof navigator !== "undefined" && navigator.locks) return await navigator.locks.request("mundely-upload-state-v4", task);
    return await task();
  });
  mutationTail = result.catch(() => undefined);
  return result;
}
async function readState(): Promise<QueueState> {
  const stored = await Preferences.get({ key: STATE_KEY });
  if (stored.value !== null) {
    const state = JSON.parse(stored.value) as QueueState;
    if (!Array.isArray(state.queue) || !state.completed || typeof state.completed !== "object") throw new Error("upload/invalid-state");
    return state;
  }
  // Legacy data stays untouched until a complete replacement state has been saved.
  let queue: QueuedUpload[] = [];
  for (const key of [QUEUE_KEY, ...LEGACY_QUEUE_KEYS]) {
    const storedQueue = await Preferences.get({ key });
    if (storedQueue.value === null) continue;
    const parsed = JSON.parse(storedQueue.value) as unknown;
    if (!Array.isArray(parsed)) throw new Error("upload/invalid-state");
    queue = parsed.map(normalizeQueueItem).filter((item): item is QueuedUpload => Boolean(item));
    if (queue.length !== parsed.length) throw new Error("upload/invalid-state");
    break;
  }
  const receipts = await Preferences.get({ key: COMPLETED_KEY });
  return { queue, completed: receipts.value ? JSON.parse(receipts.value) : {} };
}
async function writeState(state: QueueState): Promise<void> {
  // Queue removal and receipt commit share one durable write, before deleting bytes.
  await Preferences.set({ key: STATE_KEY, value: JSON.stringify(state) });
}
async function readQueue(): Promise<QueuedUpload[]> { return serialized(async () => (await readState()).queue); }
async function readCompleted(): Promise<Record<string, string>> { return serialized(async () => (await readState()).completed); }
async function unlinkPayload(localPath: string): Promise<void> {
  try { await Filesystem.deleteFile({ path: localPath, directory: Directory.Data }); }
  catch { /* A committed receipt remains valid even if cache cleanup fails. */ }
}
function ownsPath(storagePath: string, uid: string): boolean { return storagePath.startsWith(`users/${uid}/`); }

function extractUploadError(error: unknown): { code: string; message: string } {
  const candidate = error as { code?: string; message?: string } | null;
  const code = String(candidate?.code || "upload/unknown");
  const message = String(candidate?.message || error || "Upload failed").slice(0, 300);
  return { code, message };
}

export async function listQueuedUploads(storagePathPrefix?: string): Promise<QueuedUpload[]> {
  const queue = await readQueue();
  const uid = auth.currentUser?.uid;
  if (!uid) return [];
  return queue.filter((item) => ownsPath(item.storagePath, uid) && (!storagePathPrefix || item.storagePath.startsWith(storagePathPrefix)));
}

export async function discardQueuedUpload(id: string): Promise<void> {
  await serialized(async () => {
    const state = await readState();
    const target = state.queue.find((item) => item.id === id);
    if (target) state.cancellations = [...(state.cancellations || []), { path: target.storagePath, operationId: target.localPath }];
    state.queue = state.queue.filter((item) => item.id !== id);
    delete state.completed[id];
    await writeState(state);
    if (target) await unlinkPayload(target.localPath);
  });
}

export async function getCompletedUploadUrl(id: string): Promise<string | null> {
  const completed = await readCompleted();
  return completed[id] || null;
}

export async function getUploadQueueStatus(id: string): Promise<UploadQueueStatus> {
  const completed = await readCompleted();
  if (completed[id]) return { state: "completed", attempts: 0, url: completed[id] };
  const queue = await readQueue();
  const item = queue.find((entry) => entry.id === id);
  if (!item) return { state: "pending", attempts: 0 };
  return {
    state: item.lastErrorCode ? "failed" : "pending",
    attempts: item.attempts,
    lastAttemptAt: item.lastAttemptAt,
    lastErrorCode: item.lastErrorCode,
    lastErrorMessage: item.lastErrorMessage
  };
}

export async function queueFile(file: File, storagePath: string): Promise<string> {
  const id = crypto.randomUUID();
  return storePayload(id, file, storagePath);
}

async function storePayload(id: string, file: File, storagePath: string): Promise<string> {
  if (file.size > MAX_UPLOAD_BYTES) throw new Error("upload/file-too-large");
  const uid = auth.currentUser?.uid;
  if (!uid || !ownsPath(storagePath, uid)) throw new Error("upload/wrong-account");
  // Every replacement has a distinct path; an in-flight upload cannot consume new bytes.
  const localPath = `${QUEUE_DIR}/${id}-${crypto.randomUUID()}`;
  const bytes = new Uint8Array(await file.arrayBuffer());
  await Filesystem.writeFile({ path: localPath, directory: Directory.Data, data: bytesToBase64(bytes), recursive: true });
  try {
    await serialized(async () => {
      if (auth.currentUser?.uid !== uid) throw new Error("upload/wrong-account");
      const state = await readState();
      const previous = state.queue.find((entry) => entry.id === id);
      if (previous && !ownsPath(previous.storagePath, uid)) throw new Error("upload/wrong-account");
      if (previous) state.cancellations = [...(state.cancellations || []), { path: previous.storagePath, operationId: previous.localPath }];
      state.queue = state.queue.filter((entry) => entry.id !== id);
      state.queue.push({ id, storagePath, localPath, name: file.name, type: file.type || "application/octet-stream",
        size: file.size, restoreEpoch:restoreContexts.get(uid)||undefined, createdAt: new Date().toISOString(), attempts: 0 });
      delete state.completed[id];
      await writeState(state);
      if (previous) await unlinkPayload(previous.localPath);
    });
  } catch (error) {
    await unlinkPayload(localPath);
    throw error;
  }
  return id;
}

export async function getQueuedUploadBlob(id: string): Promise<Blob | null> {
  const queue = await readQueue();
  const item = queue.find((entry) => entry.id === id);
  if (!item || !auth.currentUser || !ownsPath(item.storagePath, auth.currentUser.uid)) return null;
  try {
    const stored = await Filesystem.readFile({ path: item.localPath, directory: Directory.Data });
    return await dataToBlob(stored.data, item.type);
  } catch {
    return null;
  }
}

export async function replaceQueuedUploadFile(id: string, file: File, storagePath: string): Promise<string> {
  return storePayload(id, file, storagePath);
}

export async function clearUploadError(id: string): Promise<void> {
  await serialized(async () => {
    const state = await readState();
    state.queue = state.queue.map((item) => item.id === id ? { ...item, lastErrorCode: undefined, lastErrorMessage: undefined } : item);
    await writeState(state);
  });
}

async function uploadQueuedPayload(item: QueuedUpload, blob: Blob): Promise<string> {
  return (await uploadPlanFile(blob, item.storagePath, item.localPath)).url;
}

async function performFlushUploadQueue(): Promise<Array<{ id: string; url: string }>> {
  const uid = auth.currentUser?.uid;
  if (!uid) return [];
  const cancellations = await serialized(async () => (await readState()).cancellations || []);
  for (const entry of cancellations) {
    if (auth.currentUser?.uid !== uid) return [];
    if (!ownsPath(entry.path, uid)) continue;
    try {
      await cancelPlanUpload(entry.path, entry.operationId);
      await serialized(async () => {
        const state = await readState();
        state.cancellations = (state.cancellations || []).filter(item => item.operationId !== entry.operationId);
        await writeState(state);
      });
    } catch { /* Keep cancellation durable until the server accepts the tombstone. */ }
  }
  const queue = (await readQueue()).filter((item) => ownsPath(item.storagePath, uid));
  const completed: Array<{ id: string; url: string }> = [];
  for (const item of queue) {
    if (auth.currentUser?.uid !== uid) break;
    const stillQueued = await serialized(async () => (await readState()).queue.some((entry) => entry.id === item.id && entry.localPath === item.localPath));
    if (!stillQueued) continue;
    const attemptAt = new Date().toISOString();
    let url: string;
    try {
      const stored = await Filesystem.readFile({ path: item.localPath, directory: Directory.Data });
      const blob = await dataToBlob(stored.data, item.type);
      if (auth.currentUser?.uid !== uid) break;
      url = await uploadQueuedPayload(item, blob);
    } catch (error) {
      const uploadError = extractUploadError(error);
      await serialized(async () => {
        const state = await readState();
        state.queue = state.queue.map((entry) => entry.id === item.id && entry.localPath === item.localPath
          ? { ...entry, attempts: entry.attempts + 1, lastAttemptAt: attemptAt, lastErrorCode: uploadError.code, lastErrorMessage: uploadError.message } : entry);
        await writeState(state);
      });
      continue;
    }
    const accepted = await serialized(async () => {
      const state = await readState();
      if (!state.queue.some((entry) => entry.id === item.id && entry.localPath === item.localPath)) return false;
      state.queue = state.queue.filter((entry) => entry.id !== item.id);
      state.completed[item.id] = url;
      await writeState(state);
      return true;
    });
    if (accepted) {
      await unlinkPayload(item.localPath);
      completed.push({ id: item.id, url });
    } else {
      // The user deleted/replaced this entry while its upload was in flight.
      // Persist cleanup before attempting the network call; bind it to this generation.
      const token = new URL(url).searchParams.get("token") || "";
      if (/^[a-f0-9]{64}$/.test(token)) await serialized(async () => {
        const state = await readState();
        if (state.queue.some(entry => entry.storagePath === item.storagePath)) return;
        state.cleanup = [...(state.cleanup || []).filter(entry => entry.token !== token), { path: item.storagePath, token, restoreEpoch:item.restoreEpoch }];
        await writeState(state);
      });
    }
  }
  const cleanup = await serialized(async () => (await readState()).cleanup || []);
  for (const entry of cleanup) {
    if (auth.currentUser?.uid !== uid) break;
    if (!ownsPath(entry.path, uid)) continue;
    try {
      const result = await deletePlanFile(entry.path, entry.token, entry.restoreEpoch);
      if (result?.pending) continue;
      await serialized(async () => {
        const state = await readState();
        state.cleanup = (state.cleanup || []).filter(item => item.token !== entry.token || item.restoreEpoch !== entry.restoreEpoch);
        await writeState(state);
      });
    } catch { /* Durable cleanup remains queued for the next online retry. */ }
  }
  return auth.currentUser?.uid === uid ? completed : [];
}

export async function flushUploadQueue(): Promise<Array<{ id: string; url: string }>> {
  if (activeFlush) return activeFlush;
  activeFlush = performFlushUploadQueue();
  try {
    return await activeFlush;
  } finally {
    activeFlush = null;
  }
}

export async function deleteCloudObject(storagePath: string): Promise<void> {
  if (storagePath) await deletePlanFile(storagePath);
}

export async function pendingUploadCount(): Promise<number> {
  return (await listQueuedUploads()).length;
}
